// ═══════════════════════════════════════════════════════════════════
//  modules/chat/SujRow.js
//  SujRow
// ═══════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { CATEGORY_LABELS } from "../projets/constantes";
import { WikiDocumenterBouton } from "../wiki/WikiDocumenterBouton";
import { SUJ_CATEGORIES, SUJ_SEVERITES } from "./constantes";
import { sujCanauxTexte, sujDateCourte, sujDateLongue, sujFusion } from "./utils";

// ── Ligne dépliable du registre ───────────────────────────────────────
export function SujRow({ sujet, token, isMobile, selected, onToggleSelect, onStatut, onTask, busy }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(sujet.note_staff || "");
  const [savingNote, setSavingNote] = useState(false);
  const cat = SUJ_CATEGORIES[sujet.categorie] || { label: sujet.categorie, icon: "•", color: THEME.textMuted, tache: null };
  const sev = SUJ_SEVERITES[sujet.severite] || SUJ_SEVERITES.faible;
  const catTache = cat.tache ? CATEGORY_LABELS[cat.tache] : null;

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await fetch(`${API_BASE}/api/chat/sujets/${sujet.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note_staff: note }),
      });
    } catch { /* silencieux */ }
    setSavingNote(false);
  };

  const actBtn = (label, title, color, onClick) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={title} disabled={busy}
      style={{ background: "transparent", border: `1px solid ${THEME.border}`, color, borderRadius: 6, padding: "3px 8px", cursor: busy ? "wait" : "pointer", fontSize: 12, lineHeight: 1.2 }}>{label}</button>
  );

  const actions = (
    <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
      {sujet.statut !== "ouvert" && actBtn("↺", "Réouvrir", THEME.accent, () => onStatut("ouvert"))}
      {sujet.statut !== "resolu" && actBtn("✓", "Marquer comme résolu", THEME.emerald, () => onStatut("resolu"))}
      {sujet.statut !== "ignore" && actBtn("✕", "Ignorer", THEME.textMuted, () => onStatut("ignore"))}
    </div>
  );

  const grid = "28px 40px 1fr 110px 70px 100px 108px";

  return (
    <div style={{ borderBottom: `1px solid ${THEME.border}`, background: open ? THEME.surface : "transparent" }}>
      {isMobile ? (
        <div onClick={() => setOpen(!open)} style={{ padding: "11px 14px", cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
            <input type="checkbox" checked={selected} onClick={(e) => e.stopPropagation()} onChange={onToggleSelect} />
            <span style={{ fontSize: 14 }}>{cat.icon}</span>
            <span style={{ color: THEME.text, fontSize: 13, fontWeight: 600, flex: 1 }}>{sujet.titre}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingLeft: 26 }}>
            <Badge text={sev.label} bg={sev.bg} color={sev.color} />
            <span style={{ color: THEME.text, fontSize: 12, fontWeight: 700 }}>{sujet.occurrences} occ.</span>
            <span style={{ color: THEME.textDim, fontSize: 11 }}>{sujDateCourte(sujet.last_seen)}</span>
            {sujet.reapparu ? <Badge text="réapparu" bg={THEME.amberDim} color={THEME.amber} /> : null}
            {sujFusion(sujet) ? <Badge text={sujFusion(sujet)} bg={THEME.accentDim} color={THEME.accent} /> : null}
            <div style={{ marginLeft: "auto" }}>{actions}</div>
          </div>
        </div>
      ) : (
        <div onClick={() => setOpen(!open)} style={{ display: "grid", gridTemplateColumns: grid, alignItems: "center", padding: "10px 14px", cursor: "pointer", gap: 8 }}>
          <input type="checkbox" checked={selected} onClick={(e) => e.stopPropagation()} onChange={onToggleSelect} />
          <span style={{ fontSize: 15, textAlign: "center" }} title={cat.label}>{cat.icon}</span>
          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: THEME.text, fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sujet.titre}</span>
            {sujet.reapparu ? <Badge text="réapparu" bg={THEME.amberDim} color={THEME.amber} /> : null}
            {sujFusion(sujet) ? <Badge text={sujFusion(sujet)} bg={THEME.accentDim} color={THEME.accent} /> : null}
            {sujet.task_id ? <Badge text={`📋 #${sujet.task_id}`} bg={THEME.emeraldDim} color={THEME.emerald} /> : null}
          </div>
          <div><Badge text={sev.label.toUpperCase()} bg={sev.bg} color={sev.color} /></div>
          <div style={{ color: THEME.text, fontSize: 13, fontWeight: 700, textAlign: "right" }}>{sujet.occurrences}</div>
          <div style={{ color: THEME.textDim, fontSize: 12 }}>{sujDateCourte(sujet.last_seen)}</div>
          {actions}
        </div>
      )}

      {open && (
        <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Badge text={cat.label} bg={THEME.panel} color={cat.color} />
            <Badge text={sujet.statut === "ouvert" ? "Ouvert" : sujet.statut === "resolu" ? "Résolu" : "Ignoré"}
              bg={sujet.statut === "resolu" ? THEME.emeraldDim : sujet.statut === "ignore" ? THEME.surface : THEME.amberDim}
              color={sujet.statut === "resolu" ? THEME.emerald : sujet.statut === "ignore" ? THEME.textMuted : THEME.amber} />
            {catTache
              ? <Badge text={`→ tâche « ${catTache.label} »`} bg={catTache.bg} color={catTache.color} />
              : <Badge text="rapport seulement" bg={THEME.surface} color={THEME.textDim} />}
            <span style={{ color: THEME.textDim, fontSize: 11 }}>Vu du {sujDateLongue(sujet.first_seen)} au {sujDateLongue(sujet.last_seen)}</span>
            {(sujet.canaux || []).length > 1 && (
              <span style={{ color: THEME.accent, fontSize: 11, fontWeight: 600 }}>· remonté depuis {sujCanauxTexte(sujet)} — un seul sujet, une seule tâche</span>
            )}
            {sujet.resolved_by && <span style={{ color: THEME.textDim, fontSize: 11 }}>· traité par {sujet.resolved_by}</span>}
          </div>

          {sujet.description && <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.55 }}>{sujet.description}</div>}

          {sujet.action_suggeree && (
            <div style={{ background: THEME.panel, borderLeft: `3px solid ${THEME.purple}`, borderRadius: 6, padding: "8px 12px", color: THEME.purple, fontSize: 12, fontWeight: 600 }}>
              → {sujet.action_suggeree}
            </div>
          )}

          {(sujet.joueurs || []).length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ color: THEME.textMuted, fontSize: 11 }}>Joueurs :</span>
              {sujet.joueurs.map((j) => <Badge key={j} text={j} bg={THEME.goldDim} color={THEME.gold} />)}
            </div>
          )}

          {(sujet.extraits || []).length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ color: THEME.textMuted, fontSize: 11 }}>Extraits du chat :</span>
              {sujet.extraits.map((e, i) => (
                <div key={i} style={{ background: THEME.bg, borderRadius: 6, padding: "6px 10px", color: THEME.textMuted, fontSize: 12, fontStyle: "italic", wordBreak: "break-word" }}>« {e} »</div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ color: THEME.textMuted, fontSize: 11, marginBottom: 4 }}>Note du staff</div>
              <input value={note} onChange={(e) => setNote(e.target.value)} onBlur={saveNote} placeholder="Contexte, décision prise…"
                style={{ width: "100%", background: THEME.panel, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
            </div>
            {savingNote && <span style={{ color: THEME.textDim, fontSize: 11 }}>enregistrement…</span>}
            {catTache ? (
              <button onClick={() => onTask(sujet)} disabled={!!sujet.task_id}
                style={{ background: sujet.task_id ? THEME.emeraldDim : catTache.bg, border: `1px solid ${sujet.task_id ? THEME.emerald : catTache.color}`, color: sujet.task_id ? THEME.emerald : catTache.color, borderRadius: 6, padding: "7px 14px", cursor: sujet.task_id ? "default" : "pointer", fontSize: 12, fontWeight: 700 }}>
                {sujet.task_id ? `✓ Tâche #${sujet.task_id} créée` : "＋ Créer la tâche maintenant"}
              </button>
            ) : (
              <span style={{ color: THEME.textDim, fontSize: 11, fontStyle: "italic" }}>Catégorie de lecture — ne génère pas de tâche.</span>
            )}
            <WikiDocumenterBouton sujet={sujet} />
          </div>
        </div>
      )}
    </div>
  );
}
